import { action, makeObservable, observable } from 'mobx'

import Config, { remoteProdDomain, remoteTestDomain } from '../../Config'
import StruxHubAuthService from '../../services/StruxHubAuthService'
import { isLoginBundle } from '../../utils/loginPortal'
import InitialState from '../InitialState'

export interface ITenant {
  name: string
  host: string
  /** Customer-facing label; `name` stays the stable key. */
  displayName?: string
}
export default class TenantsStore {
  @observable public activeTenant: ITenant = null
  @observable public userTenants: Array<ITenant> = []

  public constructor(
    private readonly initialState: InitialState,
    private readonly struxHubAuthService: StruxHubAuthService,
  ) {
    makeObservable(this)
    // Set initial tenant based on hostname when centralized login runtime is off
    if (
      !isLoginBundle() &&
      (location.hostname === 'localhost' ||
        [remoteProdDomain, remoteTestDomain].some(suffix =>
          location.hostname.endsWith(suffix),
        ))
    ) {
      this.activeTenant = { name: 'StruxHub', host: location.hostname }
    }
  }

  public get baseUrl(): string {
    return this.activeTenant?.host ?? Config.BASE_URL
  }

  public getTenantInfoByHostname(tenantHostName: string): ITenant {
    return this.userTenants.find(t => t.host === tenantHostName)
  }

  @action.bound
  public setTenants(userTenants: ITenant[]) {
    this.userTenants = userTenants
  }

  @action.bound
  public setActiveTenant(tenantHostName: string) {
    this.activeTenant = this.getTenantInfoByHostname(tenantHostName)
    this.persistActiveTenantToSession()
  }

  public isTenantDifferentByHost(hostToCompare: string): boolean {
    return hostToCompare !== this.activeTenant?.host
  }

  public clearTenants() {
    this.setTenants([])
    this.setActiveTenant(null)
  }

  /**
   * Mirror the active tenant host into both the in-memory user session and
   * the persisted `localStorage['stx_user_session']` blob. Centralising the
   * write here means every caller of `setActiveTenant` (picker, SSO
   * callback, single-tenant auto-select, hostname match) gets persistence
   * for free; the in-memory-only path that lost the user's pick across a
   * `restoreSession` (STX1-539) is gone.
   *
   * No-op when there is no active user session (e.g. unauthenticated picker
   * preview). When clearing the active tenant (e.g. the "Choose another
   * tenant" recovery path in Redirector) the persisted host is dropped too,
   * symmetrically — without this, restoreSession + initAuthUser would
   * re-apply the failing tenant on the next page-load and loop the user
   * back into the same dead-end. Logout still owns the full session wipe
   * (`StruxHubAuthService.logout`); this hop only clears the host field.
   */
  public persistActiveTenantToSession() {
    const user = this.initialState.tenantUserSession
    if (!user) return
    if (this.activeTenant?.host) {
      user.tenantHostOverride = this.activeTenant.host
      this.struxHubAuthService.setTenantHostOverride(this.activeTenant.host)
    } else {
      user.tenantHostOverride = null
      this.struxHubAuthService.clearTenantHostOverride()
    }
  }
}
